import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { X, Minus } from 'lucide-react';
import { useState } from 'react';
import { formatNumber } from '@/utils/formatNumber';

interface ReprocessProgressDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  total: number;
  processed: number;
  successful: number;
  failed: number;
  currentPhoto?: string;
  onStop?: () => void;
}

export function ReprocessProgressDialog({
  open,
  onOpenChange,
  total,
  processed,
  successful,
  failed,
  currentPhoto,
  onStop,
}: ReprocessProgressDialogProps) {
  const [minimized, setMinimized] = useState(false);
  const progress = total > 0 ? (processed / total) * 100 : 0;
  const isComplete = processed === total && total > 0;

  if (!open) return null;

  const handleClose = () => {
    setMinimized(true);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none">
      <Card 
        className="pointer-events-auto shadow-lg border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80"
        style={{ width: minimized ? '300px' : '400px' }}
      >
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex-1">
          <h3 className="font-semibold text-sm">
            {isComplete ? 'Reprocessing Complete' : 'Reprocessing Photos'}
          </h3>
          <p className="text-xs text-muted-foreground">
            {isComplete
              ? `${formatNumber(successful)} of ${formatNumber(total)} photos`
              : `${formatNumber(processed)} of ${formatNumber(total)} photos...`}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setMinimized(!minimized)}
          >
            <Minus className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {!minimized && (
        <div className="p-4 space-y-4">
          <Progress value={progress} className="w-full" />
          
          <div className="text-sm text-muted-foreground space-y-1">
            <div className="flex justify-between">
              <span>Total:</span>
              <span className="font-medium">{formatNumber(total)}</span>
            </div>
            <div className="flex justify-between">
              <span>Processed:</span>
              <span className="font-medium">{formatNumber(processed)}</span>
            </div>
            <div className="flex justify-between">
              <span>Successful:</span>
              <span className="font-medium text-green-600">{formatNumber(successful)}</span>
            </div>
            {failed > 0 && (
              <div className="flex justify-between">
                <span>Failed:</span>
                <span className="font-medium text-red-600">{formatNumber(failed)}</span>
              </div>
            )}
          </div>

          {currentPhoto && !isComplete && (
            <div className="text-xs text-muted-foreground truncate">
              Current: {currentPhoto}
            </div>
          )}

          {!isComplete && onStop && (
            <Button 
              onClick={onStop} 
              variant="destructive" 
              className="w-full"
            >
              Stop Processing
            </Button>
          )}
        </div>
      )}
      </Card>
    </div>
  );
}
